import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Home, Plus, Calendar, TrendingUp, Users, 
  Trash2, Edit2, ChevronLeft, ChevronRight, X, Check,
  Building, Bed, MapPin, Link, CreditCard, Receipt, 
  Settings, RefreshCw, ExternalLink, Loader2, FileText, BarChart3, Download, Printer
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { value: 'airbnb', label: 'Airbnb', color: 'bg-rose-500', commission: 3 },
  { value: 'booking', label: 'Booking.com', color: 'bg-blue-600', commission: 15 },
  { value: 'sahibinden', label: 'Sahibinden', color: 'bg-yellow-500', commission: 0 },
  { value: 'direct', label: 'Direkt', color: 'bg-emerald-500', commission: 0 },
];

const EXPENSE_CATEGORIES = [
  { value: 'cleaning', label: 'Temizlik', icon: '🧹' },
  { value: 'maintenance', label: 'Bakım/Onarım', icon: '🔧' },
  { value: 'utilities', label: 'Faturalar', icon: '💡' },
  { value: 'supplies', label: 'Malzeme', icon: '🛒' },
  { value: 'tax', label: 'Vergi', icon: '📋' },
  { value: 'insurance', label: 'Sigorta', icon: '🛡️' },
  { value: 'commission', label: 'Komisyon', icon: '💳' },
  { value: 'other', label: 'Diğer', icon: '📦' },
];

const MONTHS_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
                   'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount || 0);
};

const BelekKiralama = () => {
  const [properties, setProperties] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [revenueReport, setRevenueReport] = useState(null);
  const [stats, setStats] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [calendarData, setCalendarData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // overview, reservations, expenses, calendar, settings, invoices, reports
  const [syncing, setSyncing] = useState(false);
  const [icalPreview, setIcalPreview] = useState(null);
  
  // Modal states
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedReservationForInvoice, setSelectedReservationForInvoice] = useState(null);
  
  // Form states
  const [propertyForm, setPropertyForm] = useState({
    name: '', description: '', address: '', bedrooms: 1, capacity: 4,
    base_price: 0, summer_price: 0, winter_price: 0,
    airbnb_link: '', booking_link: '', ical_url: ''
  });
  
  const [reservationForm, setReservationForm] = useState({
    property_id: '', guest_name: '', guest_phone: '', guest_email: '',
    check_in: '', check_out: '', platform: 'direct',
    total_amount: '', platform_commission: '', notes: ''
  });
  
  const [expenseForm, setExpenseForm] = useState({
    property_id: '', category: 'cleaning', description: '',
    amount: 0, vat_amount: 0, date: '', notes: ''
  });

  const [invoiceForm, setInvoiceForm] = useState({
    reservation_id: '',
    issue_date: new Date().toISOString().split('T')[0],
    accommodation_tax_rate: 2,
    vat_rate: 0,
    notes: ''
  });

  // Data fetching
  const fetchProperties = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/belek/properties`, { withCredentials: true });
      setProperties(res.data);
      if (res.data.length > 0 && !selectedProperty) {
        setSelectedProperty(res.data[0].id);
      }
    } catch (err) {
      console.error('Properties fetch error:', err);
    }
  }, [selectedProperty]);

  const fetchReservations = useCallback(async () => {
    try {
      const params = selectedProperty ? { property_id: selectedProperty } : {};
      const res = await axios.get(`${API}/api/belek/reservations`, { 
        params, 
        withCredentials: true 
      });
      setReservations(res.data);
    } catch (err) {
      console.error('Reservations fetch error:', err);
    }
  }, [selectedProperty]);

  const fetchExpenses = useCallback(async () => {
    try {
      const params = selectedProperty ? { property_id: selectedProperty } : {};
      const res = await axios.get(`${API}/api/belek/expenses`, { 
        params, 
        withCredentials: true 
      });
      setExpenses(res.data);
    } catch (err) {
      console.error('Expenses fetch error:', err);
    }
  }, [selectedProperty]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/belek/stats`, { 
        params: { year: currentYear },
        withCredentials: true 
      });
      setStats(res.data);
    } catch (err) {
      console.error('Stats fetch error:', err);
    }
  }, [currentYear]);

  const fetchCalendar = useCallback(async () => {
    if (!selectedProperty) return;
    try {
      const res = await axios.get(`${API}/api/belek/calendar/${selectedProperty}`, { 
        params: { year: currentYear, month: currentMonth },
        withCredentials: true 
      });
      setCalendarData(res.data);
    } catch (err) {
      console.error('Calendar fetch error:', err);
    }
  }, [selectedProperty, currentYear, currentMonth]);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/belek/invoices`, { 
        params: { year: currentYear },
        withCredentials: true 
      });
      setInvoices(res.data);
    } catch (err) {
      console.error('Invoices fetch error:', err);
    }
  }, [currentYear]);

  const fetchRevenueReport = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/belek/revenue-report`, { 
        params: { year: currentYear },
        withCredentials: true 
      });
      setRevenueReport(res.data);
    } catch (err) {
      console.error('Revenue report fetch error:', err);
    }
  }, [currentYear]);

  useEffect(() => {
    fetchProperties();
    fetchStats();
  }, [fetchProperties, fetchStats]);

  useEffect(() => {
    fetchReservations();
    fetchExpenses();
    fetchCalendar();
  }, [fetchReservations, fetchExpenses, fetchCalendar]);

  useEffect(() => {
    if (activeTab === 'invoices') {
      fetchInvoices();
    } else if (activeTab === 'reports') {
      fetchRevenueReport();
    }
  }, [activeTab, fetchInvoices, fetchRevenueReport]);

  // Auto-select first property when switching to calendar tab if no property selected
  useEffect(() => {
    if (activeTab === 'calendar' && !selectedProperty && properties.length > 0) {
      setSelectedProperty(properties[0].id);
    }
  }, [activeTab, selectedProperty, properties]);

  // Fetch calendar when selectedProperty changes
  useEffect(() => {
    if (selectedProperty) {
      fetchCalendar();
    }
  }, [selectedProperty, fetchCalendar]);

  // Handlers
  const handleSaveProperty = async () => {
    try {
      const data = {
        ...propertyForm,
        base_price: parseFloat(propertyForm.base_price) || 0,
        summer_price: parseFloat(propertyForm.summer_price) || 0,
        winter_price: parseFloat(propertyForm.winter_price) || 0,
        capacity: parseInt(propertyForm.capacity) || 4,
        bedrooms: parseInt(propertyForm.bedrooms) || 1
      };
      
      if (editingItem) {
        await axios.put(`${API}/api/belek/properties/${editingItem.id}`, data, { withCredentials: true });
        toast.success('Daire güncellendi');
      } else {
        await axios.post(`${API}/api/belek/properties`, data, { withCredentials: true });
        toast.success('Daire eklendi');
      }
      setShowPropertyModal(false);
      setEditingItem(null);
      fetchProperties();
      fetchStats();
    } catch (err) {
      toast.error('Hata: ' + (err.response?.data?.detail || err.message));
    }
  };

  const openPropertyModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setPropertyForm({
        name: item.name || '',
        description: item.description || '',
        address: item.address || '',
        bedrooms: item.bedrooms || 1,
        capacity: item.capacity || 4,
        base_price: item.base_price || 0,
        summer_price: item.summer_price || 0,
        winter_price: item.winter_price || 0,
        airbnb_link: item.airbnb_link || '',
        booking_link: item.booking_link || '',
        ical_url: item.ical_url || ''
      });
    } else {
      setEditingItem(null);
      setPropertyForm({
        name: '', description: '', address: '', bedrooms: 1, capacity: 4,
        base_price: 0, summer_price: 0, winter_price: 0,
        airbnb_link: '', booking_link: '', ical_url: ''
      });
    }
    setShowPropertyModal(true);
  };

  const handleSyncAirbnb = async (propertyId) => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/api/belek/sync-ical/${propertyId}`, {}, { withCredentials: true });
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        toast.success(res.data.message);
        fetchReservations();
        fetchStats();
        fetchCalendar();
      }
    } catch (err) {
      toast.error('Senkronizasyon hatası: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSyncing(false);
    }
  };

  const handlePreviewIcal = async (propertyId) => {
    try {
      const res = await axios.get(`${API}/api/belek/ical-preview/${propertyId}`, { withCredentials: true });
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        setIcalPreview(res.data);
      }
    } catch (err) {
      toast.error('Önizleme hatası');
    }
  };

  const handleSaveReservation = async () => {
    try {
      const data = {
        ...reservationForm,
        total_amount: parseFloat(reservationForm.total_amount) || 0,
        platform_commission: parseFloat(reservationForm.platform_commission) || 0
      };
      
      if (editingItem) {
        await axios.put(`${API}/api/belek/reservations/${editingItem.id}`, data, { withCredentials: true });
        toast.success('Rezervasyon güncellendi');
      } else {
        const res = await axios.post(`${API}/api/belek/reservations`, data, { withCredentials: true });
        if (res.data.error) {
          toast.error(res.data.error);
          return;
        }
        toast.success(`Rezervasyon eklendi (${res.data.nights} gece)`);
      }
      setShowReservationModal(false);
      setEditingItem(null);
      fetchReservations();
      fetchStats();
      fetchCalendar();
    } catch (err) {
      toast.error('Hata: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSaveExpense = async () => {
    try {
      const data = {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount) || 0,
        vat_amount: parseFloat(expenseForm.vat_amount) || 0
      };
      
      if (editingItem) {
        await axios.put(`${API}/api/belek/expenses/${editingItem.id}`, data, { withCredentials: true });
        toast.success('Gider güncellendi');
      } else {
        await axios.post(`${API}/api/belek/expenses`, data, { withCredentials: true });
        toast.success('Gider eklendi');
      }
      setShowExpenseModal(false);
      setEditingItem(null);
      fetchExpenses();
      fetchStats();
    } catch (err) {
      toast.error('Hata: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteReservation = async (id) => {
    if (!window.confirm('Bu rezervasyonu silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/api/belek/reservations/${id}`, { withCredentials: true });
      toast.success('Rezervasyon silindi');
      fetchReservations();
      fetchStats();
      fetchCalendar();
    } catch (err) {
      toast.error('Silme hatası');
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Bu gideri silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/api/belek/expenses/${id}`, { withCredentials: true });
      toast.success('Gider silindi');
      fetchExpenses();
      fetchStats();
    } catch (err) {
      toast.error('Silme hatası');
    }
  };

  const handleMarkAsPaid = async (reservationId) => {
    try {
      const reservation = reservations.find(r => r.id === reservationId);
      await axios.patch(
        `${API}/api/belek/reservations/${reservationId}/payment`,
        null,
        { 
          params: { paid_amount: reservation.total_amount, payment_status: 'paid' },
          withCredentials: true 
        }
      );
      toast.success('Ödeme alındı olarak işaretlendi');
      fetchReservations();
    } catch (err) {
      toast.error('Güncelleme hatası');
    }
  };

  const openReservationModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setReservationForm({
        property_id: item.property_id,
        guest_name: item.guest_name,
        guest_phone: item.guest_phone || '',
        guest_email: item.guest_email || '',
        check_in: item.check_in,
        check_out: item.check_out,
        platform: item.platform,
        total_amount: item.total_amount,
        platform_commission: item.platform_commission,
        notes: item.notes || ''
      });
    } else {
      setEditingItem(null);
      setReservationForm({
        property_id: '', // Force user to select
        guest_name: '', guest_phone: '', guest_email: '',
        check_in: '', check_out: '', platform: 'direct',
        total_amount: '', platform_commission: '', notes: ''
      });
    }
    setShowReservationModal(true);
  };

  const openExpenseModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setExpenseForm({
        property_id: item.property_id || '',
        category: item.category,
        description: item.description,
        amount: item.amount,
        vat_amount: item.vat_amount,
        date: item.date,
        notes: item.notes || ''
      });
    } else {
      setEditingItem(null);
      setExpenseForm({
        property_id: selectedProperty || '',
        category: 'cleaning', description: '',
        amount: 0, vat_amount: 0,
        date: new Date().toISOString().split('T')[0],
        notes: ''
      });
    }
    setShowExpenseModal(true);
  };

  // Invoice handlers
  const openInvoiceModal = (reservation) => {
    setSelectedReservationForInvoice(reservation);
    setInvoiceForm({
      reservation_id: reservation.id,
      issue_date: new Date().toISOString().split('T')[0],
      accommodation_tax_rate: 2,
      vat_rate: 0,
      notes: ''
    });
    setShowInvoiceModal(true);
  };

  const handleCreateInvoice = async () => {
    try {
      const res = await axios.post(`${API}/api/belek/invoices`, invoiceForm, { withCredentials: true });
      if (res.data.error) {
        toast.error(res.data.error);
        return;
      }
      toast.success(`Fatura oluşturuldu: ${res.data.invoice.invoice_number}`);
      setShowInvoiceModal(false);
      setSelectedReservationForInvoice(null);
      fetchInvoices();
      fetchReservations();
    } catch (err) {
      toast.error('Fatura oluşturma hatası: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Bu faturayı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/api/belek/invoices/${invoiceId}`, { withCredentials: true });
      toast.success('Fatura silindi');
      fetchInvoices();
      fetchReservations();
    } catch (err) {
      toast.error('Silme hatası');
    }
  };

  const printInvoice = (invoice) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Fatura - ${invoice.invoice_number}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { margin: 0; color: #333; }
          .header p { margin: 5px 0; color: #666; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 30px; }
          .info-box h3 { margin: 0 0 10px; color: #333; font-size: 14px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
          .info-box p { margin: 5px 0; font-size: 13px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background: #f5f5f5; font-weight: bold; }
          .amount { text-align: right; }
          .total-row { font-weight: bold; background: #f9f9f9; }
          .grand-total { font-size: 18px; background: #333; color: white; }
          .footer { margin-top: 40px; text-align: center; color: #666; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>KONAKLAMA FATURASI</h1>
          <p>Fatura No: <strong>${invoice.invoice_number}</strong></p>
          <p>Düzenleme Tarihi: ${new Date(invoice.issue_date).toLocaleDateString('tr-TR')}</p>
        </div>
        
        <div class="info-grid">
          <div class="info-box">
            <h3>MİSAFİR BİLGİLERİ</h3>
            <p><strong>${invoice.guest_name}</strong></p>
            ${invoice.guest_phone ? `<p>Tel: ${invoice.guest_phone}</p>` : ''}
            ${invoice.guest_email ? `<p>E-posta: ${invoice.guest_email}</p>` : ''}
          </div>
          <div class="info-box">
            <h3>KONAKLAMA BİLGİLERİ</h3>
            <p><strong>${invoice.property_name}</strong></p>
            <p>Giriş: ${new Date(invoice.check_in).toLocaleDateString('tr-TR')}</p>
            <p>Çıkış: ${new Date(invoice.check_out).toLocaleDateString('tr-TR')}</p>
            <p>Gece Sayısı: ${invoice.nights}</p>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Açıklama</th>
              <th class="amount">Birim Fiyat</th>
              <th class="amount">Miktar</th>
              <th class="amount">Tutar</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Konaklama Hizmeti (${invoice.property_name})</td>
              <td class="amount">${invoice.nightly_rate?.toLocaleString('tr-TR')} ₺</td>
              <td class="amount">${invoice.nights} gece</td>
              <td class="amount">${invoice.subtotal?.toLocaleString('tr-TR')} ₺</td>
            </tr>
            ${invoice.accommodation_tax > 0 ? `
            <tr>
              <td>Konaklama Vergisi (%${invoice.accommodation_tax_rate})</td>
              <td class="amount">-</td>
              <td class="amount">-</td>
              <td class="amount">${invoice.accommodation_tax?.toLocaleString('tr-TR')} ₺</td>
            </tr>
            ` : ''}
            ${invoice.vat_amount > 0 ? `
            <tr>
              <td>KDV (%${invoice.vat_rate})</td>
              <td class="amount">-</td>
              <td class="amount">-</td>
              <td class="amount">${invoice.vat_amount?.toLocaleString('tr-TR')} ₺</td>
            </tr>
            ` : ''}
            <tr class="grand-total">
              <td colspan="3"><strong>GENEL TOPLAM</strong></td>
              <td class="amount"><strong>${invoice.grand_total?.toLocaleString('tr-TR')} ₺</strong></td>
            </tr>
          </tbody>
        </table>
        
        ${invoice.notes ? `<p><strong>Notlar:</strong> ${invoice.notes}</p>` : ''}
        
        <div class="footer">
          <p>Platform: ${invoice.platform?.toUpperCase() || 'DİREKT'}</p>
          <p>Bu fatura elektronik ortamda oluşturulmuştur.</p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Platform commission auto-calculate
  useEffect(() => {
    const amount = parseFloat(reservationForm.total_amount) || 0;
    if (reservationForm.platform && amount > 0) {
      const platform = PLATFORMS.find(p => p.value === reservationForm.platform);
      if (platform) {
        const commission = (amount * platform.commission) / 100;
        setReservationForm(prev => ({ ...prev, platform_commission: Math.round(commission * 100) / 100 }));
      }
    }
  }, [reservationForm.platform, reservationForm.total_amount]);

  const selectedPropertyData = properties.find(p => p.id === selectedProperty);

  return (
    <div className="p-6 space-y-6" data-testid="belek-kiralama-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Home className="w-7 h-7 text-amber-400" />
            Belek Kiralama
          </h1>
          <p className="text-zinc-400 text-sm mt-1">Günlük kiralık daire yönetimi</p>
        </div>
        
        {/* Property Selector */}
        <div className="flex items-center gap-3">
          <select
            value={selectedProperty || ''}
            onChange={(e) => setSelectedProperty(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
            data-testid="property-selector"
          >
            <option value="">Tüm Daireler</option>
            {properties.map(prop => (
              <option key={prop.id} value={prop.id}>{prop.name}</option>
            ))}
          </select>
          
          <select
            value={currentYear}
            onChange={(e) => setCurrentYear(parseInt(e.target.value))}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
          >
            {[2024, 2025, 2026, 2027].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800 pb-2 overflow-x-auto">
        {[
          { id: 'overview', label: 'Genel Bakış', icon: TrendingUp },
          { id: 'calendar', label: 'Takvim', icon: Calendar },
          { id: 'reservations', label: 'Rezervasyonlar', icon: Users },
          { id: 'invoices', label: 'Faturalar', icon: FileText },
          { id: 'reports', label: 'Gelir Raporu', icon: BarChart3 },
          { id: 'expenses', label: 'Giderler', icon: Receipt },
          { id: 'settings', label: 'Daire Ayarları', icon: Settings },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === tab.id 
                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && stats && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-400 text-sm">Toplam Gece</p>
                    <p className="text-2xl font-bold text-white">{stats.summary.total_nights}</p>
                    <p className="text-xs text-zinc-500">{stats.summary.total_reservations} rezervasyon</p>
                  </div>
                  <Calendar className="w-10 h-10 text-amber-400 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-400 text-sm">Brüt Gelir</p>
                    <p className="text-2xl font-bold text-emerald-400">{formatCurrency(stats.summary.total_revenue)}</p>
                    <p className="text-xs text-zinc-500">Komisyon: {formatCurrency(stats.summary.total_commission)}</p>
                  </div>
                  <span className="text-4xl font-bold text-emerald-400 opacity-50">₺</span>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-400 text-sm">Toplam Gider</p>
                    <p className="text-2xl font-bold text-rose-400">{formatCurrency(stats.summary.total_expenses)}</p>
                  </div>
                  <Receipt className="w-10 h-10 text-rose-400 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-400 text-sm">Net Kâr</p>
                    <p className={`text-2xl font-bold ${stats.summary.total_profit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {formatCurrency(stats.summary.total_profit)}
                    </p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-amber-400 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Property Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {Object.entries(stats.property_stats).map(([propId, propStats]) => (
              <Card key={propId} className="bg-zinc-900 border-zinc-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building className="w-5 h-5 text-amber-400" />
                    {propStats.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-zinc-400 text-xs">Doluluk</p>
                      <p className="text-xl font-bold text-white">{propStats.occupancy_rate}%</p>
                      <p className="text-xs text-zinc-500">{propStats.nights} gece</p>
                    </div>
                    <div>
                      <p className="text-zinc-400 text-xs">Gelir</p>
                      <p className="text-xl font-bold text-emerald-400">{formatCurrency(propStats.revenue)}</p>
                    </div>
                    <div>
                      <p className="text-zinc-400 text-xs">Kâr</p>
                      <p className={`text-xl font-bold ${propStats.profit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {formatCurrency(propStats.profit)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Progress bar for occupancy */}
                  <div className="mt-3">
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-amber-500 rounded-full transition-all"
                        style={{ width: `${Math.min(propStats.occupancy_rate, 100)}%` }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Platform Stats */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Platform Bazlı Performans</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {PLATFORMS.map(platform => {
                  const pStats = stats.platform_stats[platform.value] || { count: 0, nights: 0, revenue: 0, commission: 0 };
                  return (
                    <div key={platform.value} className="p-3 bg-zinc-800 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-3 h-3 rounded-full ${platform.color}`} />
                        <span className="text-white font-medium">{platform.label}</span>
                      </div>
                      <p className="text-2xl font-bold text-white">{pStats.count}</p>
                      <p className="text-xs text-zinc-400">{pStats.nights} gece</p>
                      <p className="text-sm text-emerald-400">{formatCurrency(pStats.revenue)}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Monthly Chart */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Aylık Gelir-Gider</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats.monthly_data.map(month => (
                  <div key={month.month} className="flex items-center gap-4">
                    <span className="w-16 text-sm text-zinc-400">{month.month_name.slice(0, 3)}</span>
                    <div className="flex-1 flex gap-1 h-6">
                      <div 
                        className="bg-emerald-500/50 rounded-l"
                        style={{ width: `${Math.max((month.revenue / (stats.summary.total_revenue || 1)) * 100, 1)}%` }}
                        title={`Gelir: ${formatCurrency(month.revenue)}`}
                      />
                      <div 
                        className="bg-rose-500/50 rounded-r"
                        style={{ width: `${Math.max((month.expenses / (stats.summary.total_expenses || 1)) * 100, 1)}%` }}
                        title={`Gider: ${formatCurrency(month.expenses)}`}
                      />
                    </div>
                    <span className={`w-24 text-right text-sm ${month.profit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {formatCurrency(month.profit)}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => {
                if (currentMonth === 1) {
                  setCurrentMonth(12);
                  setCurrentYear(currentYear - 1);
                } else {
                  setCurrentMonth(currentMonth - 1);
                }
              }}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-lg font-medium text-white min-w-[150px] text-center">
                {MONTHS_TR[currentMonth - 1]} {currentYear}
              </span>
              <Button variant="outline" size="icon" onClick={() => {
                if (currentMonth === 12) {
                  setCurrentMonth(1);
                  setCurrentYear(currentYear + 1);
                } else {
                  setCurrentMonth(currentMonth + 1);
                }
              }}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            
            <Button onClick={() => openReservationModal()} className="bg-amber-500 hover:bg-amber-600">
              <Plus className="w-4 h-4 mr-2" />
              Rezervasyon Ekle
            </Button>
          </div>

          {/* Calendar Grid */}
          {selectedProperty && calendarData ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{selectedPropertyData?.name} - Doluluk Takvimi</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-7 gap-1">
                  {['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'].map(day => (
                    <div key={day} className="text-center text-sm text-zinc-400 py-2">{day}</div>
                  ))}
                  
                  {/* Empty cells for first week offset */}
                  {Array.from({ length: new Date(currentYear, currentMonth - 1, 1).getDay() || 7 }).slice(1).map((_, i) => (
                    <div key={`empty-${i}`} className="h-20" />
                  ))}
                  
                  {calendarData.days.map(day => (
                    <div
                      key={day.date}
                      className={`h-20 p-1 rounded-lg border ${
                        day.is_occupied 
                          ? 'bg-amber-500/20 border-amber-500/30' 
                          : 'bg-zinc-800/50 border-zinc-700/50 hover:border-zinc-600'
                      }`}
                    >
                      <div className="text-sm font-medium text-white">{day.day}</div>
                      {day.reservation && (
                        <div className="mt-1">
                          <div className="text-xs text-amber-400 truncate">{day.reservation.guest_name}</div>
                          <div className={`text-xs px-1 rounded ${
                            PLATFORMS.find(p => p.value === day.reservation.platform)?.color || 'bg-zinc-600'
                          } text-white`}>
                            {day.reservation.platform}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Legend */}
                <div className="flex gap-4 mt-4 pt-4 border-t border-zinc-800">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-zinc-800 rounded border border-zinc-700" />
                    <span className="text-sm text-zinc-400">Boş</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-amber-500/30 rounded border border-amber-500/50" />
                    <span className="text-sm text-zinc-400">Dolu</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-8 text-center">
                <Calendar className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400 mb-4">Takvimi görmek için bir daire seçin</p>
                {properties.length > 0 && (
                  <div className="flex gap-2 justify-center">
                    {properties.map(prop => (
                      <Button 
                        key={prop.id}
                        variant="outline" 
                        onClick={() => setSelectedProperty(prop.id)}
                        className="text-amber-400 border-amber-500/50 hover:bg-amber-500/10"
                      >
                        {prop.name}
                      </Button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Reservations List for Selected Month */}
          {calendarData && calendarData.reservations.length > 0 && (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg">{MONTHS_TR[currentMonth - 1]} Rezervasyonları</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {calendarData.reservations.map(res => (
                    <div key={res.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-10 rounded ${PLATFORMS.find(p => p.value === res.platform)?.color || 'bg-zinc-600'}`} />
                        <div>
                          <p className="text-white font-medium">{res.guest_name}</p>
                          <p className="text-sm text-zinc-400">
                            {res.check_in} → {res.check_out} ({res.nights} gece)
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-emerald-400 font-medium">{formatCurrency(res.total_amount)}</p>
                        <p className={`text-xs ${res.payment_status === 'paid' ? 'text-emerald-400' : 'text-amber-400'}`}>
                          {res.payment_status === 'paid' ? 'Ödendi' : 'Bekliyor'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Reservations Tab */}
      {activeTab === 'reservations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openReservationModal()} className="bg-amber-500 hover:bg-amber-600">
              <Plus className="w-4 h-4 mr-2" />
              Rezervasyon Ekle
            </Button>
          </div>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left p-4 text-zinc-400 font-medium">Misafir</th>
                    <th className="text-left p-4 text-zinc-400 font-medium">Daire</th>
                    <th className="text-left p-4 text-zinc-400 font-medium">Tarih</th>
                    <th className="text-left p-4 text-zinc-400 font-medium">Platform</th>
                    <th className="text-right p-4 text-zinc-400 font-medium">Tutar</th>
                    <th className="text-center p-4 text-zinc-400 font-medium">Ödeme</th>
                    <th className="text-right p-4 text-zinc-400 font-medium">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.map(res => (
                    <tr key={res.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                      <td className="p-4">
                        <p className="text-white font-medium">{res.guest_name}</p>
                        <p className="text-xs text-zinc-500">{res.guest_phone}</p>
                      </td>
                      <td className="p-4 text-zinc-300">{res.property_name}</td>
                      <td className="p-4">
                        <p className="text-white">{res.check_in} → {res.check_out}</p>
                        <p className="text-xs text-zinc-500">{res.nights} gece</p>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs text-white ${PLATFORMS.find(p => p.value === res.platform)?.color || 'bg-zinc-600'}`}>
                          {PLATFORMS.find(p => p.value === res.platform)?.label || res.platform}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <p className="text-emerald-400 font-medium">{formatCurrency(res.total_amount)}</p>
                        {res.platform_commission > 0 && (
                          <p className="text-xs text-zinc-500">Kom: {formatCurrency(res.platform_commission)}</p>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        {res.payment_status === 'paid' ? (
                          <span className="text-emerald-400 flex items-center justify-center gap-1">
                            <Check className="w-4 h-4" /> Ödendi
                          </span>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleMarkAsPaid(res.id)}
                            className="text-amber-400 hover:text-amber-300"
                          >
                            <CreditCard className="w-4 h-4 mr-1" /> Ödeme Al
                          </Button>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openReservationModal(res)}>
                            <Edit2 className="w-4 h-4 text-zinc-400" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteReservation(res.id)}>
                            <Trash2 className="w-4 h-4 text-rose-400" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {reservations.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-zinc-500">
                        Henüz rezervasyon yok
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Expenses Tab */}
      {activeTab === 'expenses' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openExpenseModal()} className="bg-rose-500 hover:bg-rose-600">
              <Plus className="w-4 h-4 mr-2" />
              Gider Ekle
            </Button>
          </div>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left p-4 text-zinc-400 font-medium">Tarih</th>
                    <th className="text-left p-4 text-zinc-400 font-medium">Kategori</th>
                    <th className="text-left p-4 text-zinc-400 font-medium">Açıklama</th>
                    <th className="text-left p-4 text-zinc-400 font-medium">Daire</th>
                    <th className="text-right p-4 text-zinc-400 font-medium">Tutar</th>
                    <th className="text-right p-4 text-zinc-400 font-medium">KDV</th>
                    <th className="text-right p-4 text-zinc-400 font-medium">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map(exp => {
                    const cat = EXPENSE_CATEGORIES.find(c => c.value === exp.category);
                    return (
                      <tr key={exp.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                        <td className="p-4 text-white">{exp.date}</td>
                        <td className="p-4">
                          <span className="flex items-center gap-2">
                            <span>{cat?.icon || '📦'}</span>
                            <span className="text-zinc-300">{cat?.label || exp.category}</span>
                          </span>
                        </td>
                        <td className="p-4 text-zinc-300">{exp.description}</td>
                        <td className="p-4 text-zinc-400">{exp.property_name || 'Genel'}</td>
                        <td className="p-4 text-right text-rose-400 font-medium">{formatCurrency(exp.amount)}</td>
                        <td className="p-4 text-right text-zinc-400">{formatCurrency(exp.vat_amount)}</td>
                        <td className="p-4 text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openExpenseModal(exp)}>
                              <Edit2 className="w-4 h-4 text-zinc-400" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteExpense(exp.id)}>
                              <Trash2 className="w-4 h-4 text-rose-400" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {expenses.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-zinc-500">
                        Henüz gider kaydı yok
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div className="space-y-4">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-400" />
                Konaklama Faturaları
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Fatura oluşturulmamış rezervasyonlar */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-zinc-400 mb-3">Fatura Bekleyen Rezervasyonlar</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {reservations.filter(r => !r.invoice_number && r.status !== 'cancelled').map(res => (
                    <div key={res.id} className="p-3 bg-zinc-800 rounded-lg border border-zinc-700">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-white font-medium">{res.guest_name}</p>
                          <p className="text-xs text-zinc-400">{res.property_name}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs ${PLATFORMS.find(p => p.value === res.platform)?.color || 'bg-zinc-600'} text-white`}>
                          {res.platform}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-400 mb-2">{res.check_in} → {res.check_out}</p>
                      <div className="flex justify-between items-center">
                        <span className="text-emerald-400 font-medium">{formatCurrency(res.total_amount)}</span>
                        <Button 
                          size="sm" 
                          onClick={() => openInvoiceModal(res)}
                          className="bg-amber-500 hover:bg-amber-600"
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          Fatura Oluştur
                        </Button>
                      </div>
                    </div>
                  ))}
                  {reservations.filter(r => !r.invoice_number && r.status !== 'cancelled').length === 0 && (
                    <p className="text-zinc-500 text-sm col-span-full">Fatura bekleyen rezervasyon yok</p>
                  )}
                </div>
              </div>

              {/* Mevcut faturalar */}
              <div>
                <h3 className="text-sm font-medium text-zinc-400 mb-3">Oluşturulan Faturalar ({currentYear})</h3>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left p-3 text-zinc-400 font-medium text-sm">Fatura No</th>
                      <th className="text-left p-3 text-zinc-400 font-medium text-sm">Tarih</th>
                      <th className="text-left p-3 text-zinc-400 font-medium text-sm">Misafir</th>
                      <th className="text-left p-3 text-zinc-400 font-medium text-sm">Daire</th>
                      <th className="text-right p-3 text-zinc-400 font-medium text-sm">Tutar</th>
                      <th className="text-right p-3 text-zinc-400 font-medium text-sm">Toplam</th>
                      <th className="text-center p-3 text-zinc-400 font-medium text-sm">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(inv => (
                      <tr key={inv.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                        <td className="p-3 text-amber-400 font-medium">{inv.invoice_number}</td>
                        <td className="p-3 text-zinc-300">{new Date(inv.issue_date).toLocaleDateString('tr-TR')}</td>
                        <td className="p-3 text-white">{inv.guest_name}</td>
                        <td className="p-3 text-zinc-400">{inv.property_name}</td>
                        <td className="p-3 text-right text-zinc-300">{formatCurrency(inv.subtotal)}</td>
                        <td className="p-3 text-right text-emerald-400 font-medium">{formatCurrency(inv.grand_total)}</td>
                        <td className="p-3 text-center">
                          <div className="flex justify-center gap-1">
                            <Button variant="ghost" size="icon" onClick={() => printInvoice(inv)} title="Yazdır">
                              <Printer className="w-4 h-4 text-blue-400" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteInvoice(inv.id)} title="Sil">
                              <Trash2 className="w-4 h-4 text-rose-400" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {invoices.length === 0 && (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-zinc-500">
                          Henüz fatura oluşturulmamış
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && revenueReport && (
        <div className="space-y-6">
          {/* Yıllık Özet */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4">
                <p className="text-zinc-400 text-sm">Toplam Brüt Gelir</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(revenueReport.yearly_summary.gross_revenue)}</p>
                <p className="text-xs text-zinc-500">{revenueReport.yearly_summary.total_reservations} rezervasyon</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4">
                <p className="text-zinc-400 text-sm">Platform Komisyonları</p>
                <p className="text-2xl font-bold text-rose-400">{formatCurrency(revenueReport.yearly_summary.total_commission)}</p>
                <p className="text-xs text-zinc-500">Ort. %{revenueReport.yearly_summary.commission_rate_avg}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4">
                <p className="text-zinc-400 text-sm">Net Gelir</p>
                <p className="text-2xl font-bold text-emerald-400">{formatCurrency(revenueReport.yearly_summary.net_revenue)}</p>
                <p className="text-xs text-zinc-500">{revenueReport.yearly_summary.total_nights} gece</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4">
                <p className="text-zinc-400 text-sm">Ort. Gecelik Fiyat</p>
                <p className="text-2xl font-bold text-amber-400">{formatCurrency(revenueReport.yearly_summary.avg_nightly_rate)}</p>
                <p className="text-xs text-zinc-500">tüm platformlar</p>
              </CardContent>
            </Card>
          </div>

          {/* Platform Karşılaştırması */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-amber-400" />
                Platform Bazlı Gelir Analizi ({currentYear})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left p-3 text-zinc-400 font-medium">Platform</th>
                    <th className="text-center p-3 text-zinc-400 font-medium">Rezervasyon</th>
                    <th className="text-center p-3 text-zinc-400 font-medium">Gece</th>
                    <th className="text-right p-3 text-zinc-400 font-medium">Brüt Gelir</th>
                    <th className="text-right p-3 text-zinc-400 font-medium">Komisyon</th>
                    <th className="text-right p-3 text-zinc-400 font-medium">Net Gelir</th>
                    <th className="text-right p-3 text-zinc-400 font-medium">Kom. Oranı</th>
                  </tr>
                </thead>
                <tbody>
                  {revenueReport.platform_summary.map(p => {
                    const platformInfo = PLATFORMS.find(pl => pl.value === p.platform);
                    return (
                      <tr key={p.platform} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs text-white ${platformInfo?.color || 'bg-zinc-600'}`}>
                            {platformInfo?.label || p.platform}
                          </span>
                        </td>
                        <td className="p-3 text-center text-white">{p.reservations}</td>
                        <td className="p-3 text-center text-zinc-300">{p.nights}</td>
                        <td className="p-3 text-right text-white">{formatCurrency(p.gross_revenue)}</td>
                        <td className="p-3 text-right text-rose-400">{formatCurrency(p.commission)}</td>
                        <td className="p-3 text-right text-emerald-400 font-medium">{formatCurrency(p.net_revenue)}</td>
                        <td className="p-3 text-right text-zinc-400">%{p.commission_rate}</td>
                      </tr>
                    );
                  })}
                  {revenueReport.platform_summary.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-zinc-500">
                        Henüz rezervasyon yok
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Aylık Rapor */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Aylık Gelir Raporu ({currentYear})</CardTitle>
            </CardHeader>
            <CardContent>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left p-3 text-zinc-400 font-medium">Ay</th>
                    <th className="text-center p-3 text-zinc-400 font-medium">Rez.</th>
                    <th className="text-center p-3 text-zinc-400 font-medium">Gece</th>
                    <th className="text-right p-3 text-zinc-400 font-medium">Brüt Gelir</th>
                    <th className="text-right p-3 text-zinc-400 font-medium">Komisyon</th>
                    <th className="text-right p-3 text-zinc-400 font-medium">Net Gelir</th>
                    <th className="text-right p-3 text-zinc-400 font-medium">Ort. Gece</th>
                  </tr>
                </thead>
                <tbody>
                  {revenueReport.monthly_report.map(m => (
                    <tr key={m.month} className={`border-b border-zinc-800/50 ${m.gross_revenue > 0 ? 'hover:bg-zinc-800/30' : 'opacity-50'}`}>
                      <td className="p-3 text-white font-medium">{m.month_name}</td>
                      <td className="p-3 text-center text-zinc-300">{m.reservations_count}</td>
                      <td className="p-3 text-center text-zinc-300">{m.total_nights}</td>
                      <td className="p-3 text-right text-white">{formatCurrency(m.gross_revenue)}</td>
                      <td className="p-3 text-right text-rose-400">{formatCurrency(m.total_commission)}</td>
                      <td className="p-3 text-right text-emerald-400 font-medium">{formatCurrency(m.net_revenue)}</td>
                      <td className="p-3 text-right text-amber-400">{formatCurrency(m.avg_nightly_rate)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-zinc-800/50 font-bold">
                    <td className="p-3 text-white">TOPLAM</td>
                    <td className="p-3 text-center text-white">{revenueReport.yearly_summary.total_reservations}</td>
                    <td className="p-3 text-center text-white">{revenueReport.yearly_summary.total_nights}</td>
                    <td className="p-3 text-right text-white">{formatCurrency(revenueReport.yearly_summary.gross_revenue)}</td>
                    <td className="p-3 text-right text-rose-400">{formatCurrency(revenueReport.yearly_summary.total_commission)}</td>
                    <td className="p-3 text-right text-emerald-400">{formatCurrency(revenueReport.yearly_summary.net_revenue)}</td>
                    <td className="p-3 text-right text-amber-400">{formatCurrency(revenueReport.yearly_summary.avg_nightly_rate)}</td>
                  </tr>
                </tfoot>
              </table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white">Daire Ayarları & Fiyatlandırma</h2>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/api/belek/recalculate-prices`, {}, { withCredentials: true });
                    if (res.data.success) {
                      toast.success(`${res.data.updated} Airbnb rezervasyonu fiyatlandırıldı!`);
                      fetchReservations();
                      fetchStats();
                    } else {
                      toast.error(res.data.error || 'Hata oluştu');
                    }
                  } catch (err) {
                    toast.error('Fiyatlandırma hatası: ' + (err.response?.data?.detail || err.message));
                  }
                }}
                className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Airbnb Fiyatlarını Hesapla
              </Button>
              <Button onClick={() => openPropertyModal()} className="bg-amber-500 hover:bg-amber-600">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Daire Ekle
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {properties.map(prop => (
              <Card key={prop.id} className="bg-zinc-900 border-zinc-800">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building className="w-5 h-5 text-amber-400" />
                      {prop.name}
                    </CardTitle>
                    <Button variant="ghost" size="icon" onClick={() => openPropertyModal(prop)}>
                      <Edit2 className="w-4 h-4 text-zinc-400" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Fiyatlar */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 bg-zinc-800 rounded-lg text-center">
                      <p className="text-xs text-zinc-400">Temel Fiyat</p>
                      <p className="text-lg font-bold text-white">{formatCurrency(prop.base_price)}</p>
                      <p className="text-xs text-zinc-500">/gece</p>
                    </div>
                    <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-center">
                      <p className="text-xs text-amber-400">Yaz Sezonu</p>
                      <p className="text-lg font-bold text-amber-400">{formatCurrency(prop.summer_price)}</p>
                      <p className="text-xs text-zinc-500">Haz-Eyl</p>
                    </div>
                    <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-center">
                      <p className="text-xs text-blue-400">Kış Sezonu</p>
                      <p className="text-lg font-bold text-blue-400">{formatCurrency(prop.winter_price)}</p>
                      <p className="text-xs text-zinc-500">Eki-May</p>
                    </div>
                  </div>

                  {/* Daire Bilgileri */}
                  <div className="flex items-center gap-4 text-sm text-zinc-400">
                    <span className="flex items-center gap-1">
                      <Bed className="w-4 h-4" /> {prop.bedrooms} Yatak Odası
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" /> {prop.capacity} Kişi
                    </span>
                  </div>

                  {/* Platform Linkleri */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-zinc-300">Platform Bağlantıları</p>
                    
                    {prop.airbnb_link && (
                      <div className="flex items-center justify-between p-2 bg-rose-500/10 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-rose-500" />
                          <span className="text-sm text-zinc-300">Airbnb</span>
                        </div>
                        <a href={prop.airbnb_link} target="_blank" rel="noopener noreferrer" 
                           className="text-rose-400 hover:text-rose-300">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    )}
                    
                    {prop.booking_link && (
                      <div className="flex items-center justify-between p-2 bg-blue-500/10 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-blue-500" />
                          <span className="text-sm text-zinc-300">Booking.com</span>
                        </div>
                        <a href={prop.booking_link} target="_blank" rel="noopener noreferrer"
                           className="text-blue-400 hover:text-blue-300">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    )}
                    
                    {!prop.airbnb_link && !prop.booking_link && (
                      <p className="text-sm text-zinc-500 italic">Henüz platform linki eklenmedi</p>
                    )}
                  </div>

                  {/* Airbnb iCal Senkronizasyonu - Airbnb'den Al */}
                  <div className="border-t border-zinc-800 pt-4">
                    <p className="text-sm font-medium text-zinc-300 mb-2">📥 Airbnb'den Rezervasyon Çek</p>
                    
                    {prop.ical_url ? (
                      <div className="space-y-2">
                        <div className="p-2 bg-zinc-800 rounded-lg">
                          <p className="text-xs text-zinc-500 truncate">{prop.ical_url}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleSyncAirbnb(prop.id)}
                            disabled={syncing}
                            className="flex-1"
                          >
                            {syncing ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4 mr-2" />
                            )}
                            Senkronize Et
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handlePreviewIcal(prop.id)}
                          >
                            Önizle
                          </Button>
                        </div>
                        {prop.last_ical_sync && (
                          <p className="text-xs text-zinc-500">
                            Son senkronizasyon: {new Date(prop.last_ical_sync).toLocaleString('tr-TR')}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="p-3 bg-zinc-800/50 rounded-lg text-center">
                        <p className="text-sm text-zinc-500 mb-2">iCal URL'i henüz eklenmedi</p>
                        <Button variant="outline" size="sm" onClick={() => openPropertyModal(prop)}>
                          <Link className="w-4 h-4 mr-2" />
                          iCal URL Ekle
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Airbnb'ye Blok Gönder - Export URL */}
                  <div className="border-t border-zinc-800 pt-4">
                    <p className="text-sm font-medium text-zinc-300 mb-2">📤 Airbnb'ye Rezervasyon Gönder</p>
                    <p className="text-xs text-zinc-500 mb-3">
                      Bu URL'yi Airbnb'ye ekleyerek, buraya girdiğiniz rezervasyonlar otomatik olarak Airbnb'de bloklanır.
                    </p>
                    <div className="p-2 bg-emerald-900/30 border border-emerald-700/50 rounded-lg">
                      <p className="text-xs text-emerald-400 font-mono truncate">
                        {`${API}/api/belek/ical-export/${prop.id}.ics`}
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2 w-full"
                      onClick={() => {
                        navigator.clipboard.writeText(`${API}/api/belek/ical-export/${prop.id}.ics`);
                        toast.success('URL kopyalandı! Airbnb\'ye ekleyebilirsiniz.');
                      }}
                    >
                      <Link className="w-4 h-4 mr-2" />
                      URL'yi Kopyala
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Airbnb iCal Nasıl Bulunur? */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Link className="w-5 h-5 text-rose-400" />
                Airbnb iCal URL'i Nasıl Bulunur?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-zinc-400">
              <ol className="list-decimal list-inside space-y-2">
                <li>Airbnb hesabınıza giriş yapın</li>
                <li><strong>İlanlar</strong> {'>'} İlanınızı seçin {'>'} <strong>Takvim</strong> bölümüne gidin</li>
                <li>Sağ üst köşede <strong>⚙️ Ayarlar</strong> ikonuna tıklayın</li>
                <li><strong>"Takvimi dışa aktar"</strong> seçeneğini bulun</li>
                <li><strong>"Takvim bağlantısını kopyala"</strong> butonuna tıklayın</li>
                <li>Kopyalanan linki buraya yapıştırın</li>
              </ol>
              <p className="text-zinc-500 mt-3">
                Not: Sahibinden'de rezervasyon modülü bulunmadığından, sadece Airbnb'den otomatik senkronizasyon yapılabilir.
                Sahibinden üzerinden gelen rezervasyonları manuel olarak ekleyebilirsiniz.
              </p>
            </CardContent>
          </Card>

          {/* Bildirim Ayarları */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                🔔 Bildirim Ayarları
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-zinc-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <span className="text-white font-medium">E-posta Bildirimleri</span>
                  </div>
                  <p className="text-sm text-zinc-400">bbitik82@gmail.com</p>
                  <p className="text-xs text-zinc-500 mt-1">Yeni rezervasyon, giriş/çıkış hatırlatması</p>
                </div>
                
                <div className="p-4 bg-zinc-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <span className="text-white font-medium">Telefon</span>
                  </div>
                  <p className="text-sm text-zinc-400">+90 532 449 60 49</p>
                  <p className="text-xs text-zinc-500 mt-1">SMS/WhatsApp (yakında)</p>
                </div>
              </div>
              
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-sm text-amber-400 font-medium mb-2">📅 Otomatik Bildirimler</p>
                <ul className="text-sm text-zinc-400 space-y-1">
                  <li>✓ Yeni rezervasyon eklendiğinde anında e-posta</li>
                  <li>✓ Giriş tarihinden 1 gün önce sabah 09:00'da hatırlatma</li>
                  <li>✓ Çıkış tarihinde sabah 09:00'da temizlik hatırlatması</li>
                </ul>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/api/belek/notifications/send-test`, {}, { withCredentials: true });
                      if (res.data.error) {
                        toast.error(res.data.error);
                      } else {
                        toast.success(res.data.message);
                      }
                    } catch (err) {
                      toast.error('Test bildirimi gönderilemedi');
                    }
                  }}
                >
                  📧 Test E-postası Gönder
                </Button>
                
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/api/belek/notifications/check-now`, {}, { withCredentials: true });
                      toast.success(res.data.message);
                    } catch (err) {
                      toast.error('Bildirim kontrolü yapılamadı');
                    }
                  }}
                >
                  🔄 Manuel Kontrol Yap
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* iCal Preview Modal */}
      <Dialog open={!!icalPreview} onOpenChange={() => setIcalPreview(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle>Airbnb Takvim Önizlemesi</DialogTitle>
          </DialogHeader>
          
          {icalPreview && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">
                {icalPreview.property_name} için {icalPreview.total_events} etkinlik bulundu
              </p>
              
              <div className="max-h-80 overflow-y-auto space-y-2">
                {icalPreview.events?.map((event, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                    <div>
                      <p className="text-white font-medium">{event.guest_name}</p>
                      <p className="text-sm text-zinc-400">
                        {event.check_in} → {event.check_out}
                      </p>
                    </div>
                    <span className="text-amber-400 font-medium">{event.nights} gece</span>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setIcalPreview(null)}>Kapat</Button>
                <Button 
                  onClick={() => {
                    handleSyncAirbnb(properties.find(p => p.name === icalPreview.property_name)?.id);
                    setIcalPreview(null);
                  }}
                  className="bg-amber-500 hover:bg-amber-600"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Senkronize Et
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reservation Modal */}
      <Dialog open={showReservationModal} onOpenChange={setShowReservationModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Rezervasyon Düzenle' : 'Yeni Rezervasyon'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Daire *</Label>
              <select
                value={reservationForm.property_id}
                onChange={(e) => setReservationForm({ ...reservationForm, property_id: e.target.value })}
                className="w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                required
              >
                <option value="" disabled>-- Daire Seçin --</option>
                {properties.map(prop => (
                  <option key={prop.id} value={prop.id}>{prop.name} ({prop.type})</option>
                ))}
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Giriş Tarihi</Label>
                <Input
                  type="date"
                  value={reservationForm.check_in}
                  onChange={(e) => setReservationForm({ ...reservationForm, check_in: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label>Çıkış Tarihi</Label>
                <Input
                  type="date"
                  value={reservationForm.check_out}
                  onChange={(e) => setReservationForm({ ...reservationForm, check_out: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            
            <div>
              <Label>Misafir Adı</Label>
              <Input
                value={reservationForm.guest_name}
                onChange={(e) => setReservationForm({ ...reservationForm, guest_name: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Ad Soyad"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Telefon</Label>
                <Input
                  value={reservationForm.guest_phone}
                  onChange={(e) => setReservationForm({ ...reservationForm, guest_phone: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="+90..."
                />
              </div>
              <div>
                <Label>E-posta</Label>
                <Input
                  value={reservationForm.guest_email}
                  onChange={(e) => setReservationForm({ ...reservationForm, guest_email: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="email@..."
                />
              </div>
            </div>
            
            <div>
              <Label>Platform</Label>
              <select
                value={reservationForm.platform}
                onChange={(e) => setReservationForm({ ...reservationForm, platform: e.target.value })}
                className="w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
              >
                {PLATFORMS.map(p => (
                  <option key={p.value} value={p.value}>{p.label} ({p.commission}% komisyon)</option>
                ))}
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Toplam Tutar (₺)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={reservationForm.total_amount === 0 ? '' : reservationForm.total_amount}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
                    setReservationForm({ ...reservationForm, total_amount: val === '' ? 0 : parseFloat(val) || 0 });
                  }}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="0"
                />
              </div>
              <div>
                <Label>Platform Komisyonu (₺)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={reservationForm.platform_commission === 0 ? '' : reservationForm.platform_commission}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
                    setReservationForm({ ...reservationForm, platform_commission: val === '' ? 0 : parseFloat(val) || 0 });
                  }}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Otomatik hesaplanır"
                />
              </div>
            </div>
            
            <div>
              <Label>Notlar</Label>
              <Input
                value={reservationForm.notes}
                onChange={(e) => setReservationForm({ ...reservationForm, notes: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Ek notlar..."
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowReservationModal(false)}>İptal</Button>
              <Button onClick={handleSaveReservation} className="bg-amber-500 hover:bg-amber-600">
                {editingItem ? 'Güncelle' : 'Kaydet'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Expense Modal */}
      <Dialog open={showExpenseModal} onOpenChange={setShowExpenseModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Gider Düzenle' : 'Yeni Gider'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Daire (Opsiyonel)</Label>
              <select
                value={expenseForm.property_id}
                onChange={(e) => setExpenseForm({ ...expenseForm, property_id: e.target.value })}
                className="w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
              >
                <option value="">Genel Gider</option>
                {properties.map(prop => (
                  <option key={prop.id} value={prop.id}>{prop.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label>Kategori</Label>
              <select
                value={expenseForm.category}
                onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })}
                className="w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
              >
                {EXPENSE_CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.icon} {cat.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label>Açıklama</Label>
              <Input
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Gider açıklaması..."
              />
            </div>
            
            <div>
              <Label>Tarih</Label>
              <Input
                type="date"
                value={expenseForm.date}
                onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tutar (₺)</Label>
                <Input
                  type="number"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: parseFloat(e.target.value) || 0 })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label>KDV (₺)</Label>
                <Input
                  type="number"
                  value={expenseForm.vat_amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, vat_amount: parseFloat(e.target.value) || 0 })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            
            <div>
              <Label>Notlar</Label>
              <Input
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Ek notlar..."
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowExpenseModal(false)}>İptal</Button>
              <Button onClick={handleSaveExpense} className="bg-rose-500 hover:bg-rose-600">
                {editingItem ? 'Güncelle' : 'Kaydet'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Property Modal - Daire Düzenleme */}
      <Dialog open={showPropertyModal} onOpenChange={setShowPropertyModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Daire Düzenle' : 'Yeni Daire'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Daire Adı</Label>
              <Input
                value={propertyForm.name}
                onChange={(e) => setPropertyForm({ ...propertyForm, name: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Örn: 2+1 Daire"
              />
            </div>
            
            <div>
              <Label>Açıklama</Label>
              <Input
                value={propertyForm.description}
                onChange={(e) => setPropertyForm({ ...propertyForm, description: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Kısa açıklama..."
              />
            </div>
            
            <div>
              <Label>Adres</Label>
              <Input
                value={propertyForm.address}
                onChange={(e) => setPropertyForm({ ...propertyForm, address: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Belek, Antalya"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Yatak Odası</Label>
                <Input
                  type="number"
                  value={propertyForm.bedrooms}
                  onChange={(e) => setPropertyForm({ ...propertyForm, bedrooms: parseInt(e.target.value) || 1 })}
                  className="bg-zinc-800 border-zinc-700"
                  min={1}
                />
              </div>
              <div>
                <Label>Kapasite (Kişi)</Label>
                <Input
                  type="number"
                  value={propertyForm.capacity}
                  onChange={(e) => setPropertyForm({ ...propertyForm, capacity: parseInt(e.target.value) || 4 })}
                  className="bg-zinc-800 border-zinc-700"
                  min={1}
                />
              </div>
            </div>
            
            <div className="border-t border-zinc-800 pt-4">
              <p className="text-sm font-medium text-zinc-300 mb-3">Fiyatlandırma (₺/gece)</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Temel Fiyat</Label>
                  <Input
                    type="number"
                    value={propertyForm.base_price}
                    onChange={(e) => setPropertyForm({ ...propertyForm, base_price: parseFloat(e.target.value) || 0 })}
                    className="bg-zinc-800 border-zinc-700"
                    min={0}
                  />
                </div>
                <div>
                  <Label className="text-xs text-amber-400">Yaz Sezonu</Label>
                  <Input
                    type="number"
                    value={propertyForm.summer_price}
                    onChange={(e) => setPropertyForm({ ...propertyForm, summer_price: parseFloat(e.target.value) || 0 })}
                    className="bg-zinc-800 border-zinc-700 border-amber-500/30"
                    min={0}
                  />
                </div>
                <div>
                  <Label className="text-xs text-blue-400">Kış Sezonu</Label>
                  <Input
                    type="number"
                    value={propertyForm.winter_price}
                    onChange={(e) => setPropertyForm({ ...propertyForm, winter_price: parseFloat(e.target.value) || 0 })}
                    className="bg-zinc-800 border-zinc-700 border-blue-500/30"
                    min={0}
                  />
                </div>
              </div>
            </div>
            
            <div className="border-t border-zinc-800 pt-4">
              <p className="text-sm font-medium text-zinc-300 mb-3">Platform Linkleri</p>
              
              <div className="space-y-3">
                <div>
                  <Label className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-rose-500" />
                    Airbnb İlan Linki
                  </Label>
                  <Input
                    value={propertyForm.airbnb_link}
                    onChange={(e) => setPropertyForm({ ...propertyForm, airbnb_link: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="https://airbnb.com/rooms/..."
                  />
                </div>
                
                <div>
                  <Label className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    Booking.com İlan Linki
                  </Label>
                  <Input
                    value={propertyForm.booking_link}
                    onChange={(e) => setPropertyForm({ ...propertyForm, booking_link: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="https://booking.com/hotel/..."
                  />
                </div>
              </div>
            </div>
            
            <div className="border-t border-zinc-800 pt-4">
              <p className="text-sm font-medium text-zinc-300 mb-3">Airbnb iCal Senkronizasyonu</p>
              <div>
                <Label className="flex items-center gap-2 text-rose-400">
                  <RefreshCw className="w-4 h-4" />
                  iCal URL (Takvim Senkronizasyonu)
                </Label>
                <Input
                  value={propertyForm.ical_url}
                  onChange={(e) => setPropertyForm({ ...propertyForm, ical_url: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="https://www.airbnb.com/calendar/ical/..."
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Airbnb'den aldığınız iCal takvim linkini buraya yapıştırın
                </p>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowPropertyModal(false)}>İptal</Button>
              <Button onClick={handleSaveProperty} className="bg-amber-500 hover:bg-amber-600">
                {editingItem ? 'Güncelle' : 'Kaydet'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-400" />
              Konaklama Faturası Oluştur
            </DialogTitle>
          </DialogHeader>
          
          {selectedReservationForInvoice && (
            <div className="space-y-4">
              {/* Rezervasyon Bilgileri */}
              <div className="p-4 bg-zinc-800 rounded-lg">
                <h4 className="text-sm font-medium text-zinc-400 mb-2">Rezervasyon Bilgileri</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-zinc-500">Misafir:</span>
                    <span className="text-white ml-2">{selectedReservationForInvoice.guest_name}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Daire:</span>
                    <span className="text-white ml-2">{selectedReservationForInvoice.property_name}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Giriş:</span>
                    <span className="text-white ml-2">{selectedReservationForInvoice.check_in}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Çıkış:</span>
                    <span className="text-white ml-2">{selectedReservationForInvoice.check_out}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Gece:</span>
                    <span className="text-white ml-2">{selectedReservationForInvoice.nights}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Platform:</span>
                    <span className="text-white ml-2">{selectedReservationForInvoice.platform}</span>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-zinc-700">
                  <span className="text-zinc-500">Toplam Tutar:</span>
                  <span className="text-emerald-400 font-bold ml-2">{formatCurrency(selectedReservationForInvoice.total_amount)}</span>
                </div>
              </div>

              {/* Fatura Ayarları */}
              <div>
                <Label>Fatura Tarihi</Label>
                <Input
                  type="date"
                  value={invoiceForm.issue_date}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, issue_date: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Konaklama Vergisi (%)</Label>
                  <Input
                    type="number"
                    value={invoiceForm.accommodation_tax_rate}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, accommodation_tax_rate: parseFloat(e.target.value) || 0 })}
                    className="bg-zinc-800 border-zinc-700"
                    min="0"
                    max="10"
                    step="0.5"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Vergi: {formatCurrency(selectedReservationForInvoice.total_amount * (invoiceForm.accommodation_tax_rate / 100))}
                  </p>
                </div>
                <div>
                  <Label>KDV Oranı (%)</Label>
                  <Input
                    type="number"
                    value={invoiceForm.vat_rate}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, vat_rate: parseFloat(e.target.value) || 0 })}
                    className="bg-zinc-800 border-zinc-700"
                    min="0"
                    max="20"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    KDV: {formatCurrency(selectedReservationForInvoice.total_amount * (invoiceForm.vat_rate / 100))}
                  </p>
                </div>
              </div>

              <div>
                <Label>Notlar (Opsiyonel)</Label>
                <Input
                  value={invoiceForm.notes}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Fatura notu..."
                />
              </div>

              {/* Toplam */}
              <div className="p-4 bg-emerald-900/20 border border-emerald-700/30 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-zinc-300">Fatura Toplamı:</span>
                  <span className="text-2xl font-bold text-emerald-400">
                    {formatCurrency(
                      selectedReservationForInvoice.total_amount * 
                      (1 + (invoiceForm.accommodation_tax_rate / 100) + (invoiceForm.vat_rate / 100))
                    )}
                  </span>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowInvoiceModal(false)}>İptal</Button>
                <Button onClick={handleCreateInvoice} className="bg-amber-500 hover:bg-amber-600">
                  <FileText className="w-4 h-4 mr-2" />
                  Fatura Oluştur
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BelekKiralama;
